import { ERROR_CODES } from '@diario/common';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { EstadoError } from '../../components/ui/estadoError';
import { useExperienceDetail } from '../../features/experiences/api/useExperienceDetail';
import { useExperience } from '../../features/experiences/api/useExperiences';
import {
	type ExperienciaTablon,
	TablonExperiencia,
} from '../../features/experiences/components/tablon/tablonExperiencia';
import { traducirCodigo } from '../../lib/errores';
import { ApiError } from '../../lib/http';

/**
 * `/experiencia/:id/tablon` (documento 03c, decisión 6 de la oleada 6), bajo el layout
 * autenticado — el tablón es del organizador (o de administración), nunca de quien no tiene
 * sesión. `ExperienciaTablon` combina dos peticiones que, por separado, no alcanzan: el DTO
 * básico (`GET /experiences/:id`, `useExperience`, trae `status`, `accessLabel`,
 * `confirmedCount`, `privateShareToken`…) y la ficha redactada (`GET /experiences/:id/detail`,
 * `useExperienceDetail`) por los campos que solo ella expone —
 * `meetingPoint`/`howToArrive`/`whatsappGroupUrl`/`zone` (T-4.2: la zona aproximada, para
 * `location` de la `ExperienceCard` de la cabecera) — según el visor.
 *
 * Auditoría funcional 2026-09-10 (B2/H-03): desde esa oleada, `GET /experiences/:id` ya no es de
 * "sesión obligatoria" sin más — responde solo a quien organiza el plan o a un ADMIN, y un 404
 * neutro a cualquier otra sesión válida, como si el plan no existiera (RN-7, mismo criterio que
 * `PATCH`). Ese 404 es, pues, la respuesta NORMAL a quien no gestiona la experiencia, no un fallo:
 * el bloque de error de abajo lo trata igual que un 403, con la misma pantalla neutra y sin
 * reintento (`useExperience` desactiva los reintentos por defecto en `useExperiences.ts` para que
 * esa visita no espere ~8 s ni dispare 4 peticiones idénticas antes de mostrarla). Ninguna de las
 * dos peticiones garantiza que quien pregunta pueda editar nada: `TablonExperiencia` ya asume que
 * quien la monta tiene motivo para verla (organizador o admin), igual que el resto de piezas de
 * esta oleada.
 */
export const Route = createFileRoute('/_autenticado/experiencia/$id/tablon')({ component: PantallaTablon });

function PantallaTablon(): React.JSX.Element {
	const { t } = useTranslation(['experiences', 'common']);
	const { id } = Route.useParams();
	const basica = useExperience(id);
	const detalle = useExperienceDetail(id);

	if (basica.isPending || detalle.isPending) {
		return <EstadoError mensaje={t('ficha.estados.cargando')} variante='carga' />;
	}

	if (basica.isError || detalle.isError) {
		// Mismo criterio que `/unirse/:id` (RN-7 de la iteración 02): un 404 (no existe) y un 403
		// (existe pero sin acceso) producen el MISMO mensaje neutro, sin reintento — insistir no
		// cambia nada. Cualquier otro código (red, 500…) es un fallo distinto: reintentable,
		// relanzando las dos peticiones a la vez, nunca solo la que falló.
		const error = basica.error ?? detalle.error;
		const codigo = error instanceof ApiError ? error.code : undefined;
		if (codigo === ERROR_CODES.notFound || codigo === ERROR_CODES.forbidden) {
			return (
				<EstadoError
					mensaje={t('ficha.estados.noEncontrada')}
					salida={{ etiqueta: t('ficha.estados.salida'), to: '/' }}
				/>
			);
		}
		return (
			<EstadoError
				mensaje={t(`common:errores.${traducirCodigo(codigo ?? ERROR_CODES.internal)}`)}
				reintentar={{
					etiqueta: t('ficha.estados.reintentar'),
					onClick: () => {
						void basica.refetch();
						void detalle.refetch();
					},
				}}
				salida={{ etiqueta: t('ficha.estados.salida'), to: '/' }}
				variante='error'
			/>
		);
	}

	const experiencia: ExperienciaTablon = {
		...basica.data,
		howToArrive: detalle.data.howToArrive,
		meetingPoint: detalle.data.meetingPoint,
		whatsappGroupUrl: detalle.data.whatsappGroupUrl,
		zone: detalle.data.zone,
	};

	return <TablonExperiencia experiencia={experiencia} />;
}
